import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Log } from '@/utils/logger';

// Role cache to reduce database calls
const roleCache = new Map<string, { role: string; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useOptimizedUserRole = () => {
  const { user } = useAuth();
  const [role, setRole] = useState<string>('user');
  const [isLoading, setIsLoading] = useState(true);
  const abortControllerRef = useRef<AbortController>();

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) {
        setRole('user');
        setIsLoading(false);
        return;
      }

      // Check cache first
      const cached = roleCache.get(user.id);
      const now = Date.now();
      
      if (cached && (now - cached.timestamp) < CACHE_DURATION) {
        setRole(cached.role);
        setIsLoading(false);
        return;
      }

      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          Log.error('Error fetching user role', { error: error.message });
        }

        const userRole = data?.role || 'user';
        
        // Update cache
        roleCache.set(user.id, {
          role: userRole,
          timestamp: now
        });

        setRole(userRole);
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          Log.error('useOptimizedUserRole - Error', { error: error.message });
          setRole('user');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserRole();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [user]);

  // Clear cache when user changes
  useEffect(() => {
    if (user) {
      // Keep only current user's cache entry
      const userCache = roleCache.get(user.id);
      roleCache.clear();
      if (userCache) {
        roleCache.set(user.id, userCache);
      }
    } else {
      roleCache.clear();
    }
  }, [user?.id]);

  return { 
    role, 
    isAdmin: role === 'admin', 
    isLoading,
    // Function to manually invalidate cache if needed
    invalidateCache: () => roleCache.delete(user?.id || '')
  };
};
